/* eslint-disable max-lines */
import { Edit } from "lucide-react";
import { useState } from "react";

import { Textarea } from "@design-lab/shared";
import { useTab } from "@design-lab/shared/ui/layouts/state";
import { track } from "jiffy-analytics";
import { useTokenManagement } from "../../model/tokenManagement";
import { CreditsHeader, ErrorMessage, getGenerateButtonContent } from "./shared";

export type PromptFormMobileProps = {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onGenerate: (prompt: string) => void;
  isLoading?: boolean;
  error?: string | undefined;
  hasGeneratedImages?: boolean;
};

export function PromptFormMobile({
  prompt,
  onPromptChange,
  onGenerate,
  isLoading = false,
  error = undefined,
  hasGeneratedImages = false
}: PromptFormMobileProps) {
  const { aiCreditsBalance } = useTokenManagement();
  const [isEditing, setIsEditing] = useState(false);
  const [originalPrompt, setOriginalPrompt] = useState("");
  const hasCredits = aiCreditsBalance > 0;
  const { isValid } = useTab();

  const handleGenerate = () => {
    if (prompt.trim()) {
      onGenerate(prompt);
      setIsEditing(false);
    }
  };

  const handleEditClick = () => {
    setOriginalPrompt(prompt);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    onPromptChange(originalPrompt);
    setIsEditing(false);
  };

  const handleFocus = () => {
    track("weave_generate_input_focused");
  };

  // Determine mobile UI state
  const shouldShowCompactMode = (hasGeneratedImages || isValid) && !isEditing;

  return (
    <div className="flex flex-1 flex-col">
      {!shouldShowCompactMode && (
        <CreditsHeader labelHtmlFor="prompt" helpTestId="mobile-help-trigger" />
      )}

      <div className="relative mx-0.5 mb-1 flex">
        {shouldShowCompactMode ? (
          <div className="bg-cta-gradient w-full rounded-lg p-0.5">
            <div
              className="min-h-18 line-clamp-3 w-full overflow-hidden break-words rounded-md bg-white p-2 pr-12 text-xs font-normal leading-5 text-gray-700"
              data-testid="prompt-display"
            >
              {prompt || (
                <span className="text-gray-400">
                  e.g., A majestic lion wearing a crown, vintage comic book style
                </span>
              )}
            </div>
            <button
              onClick={handleEditClick}
              className="bg-cta-gradient absolute right-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full text-white drop-shadow-2xl"
              data-testid="edit-prompt-button"
            >
              <Edit className="size-4" />
            </button>
          </div>
        ) : (
          <Textarea
            id="prompt"
            value={prompt}
            onChange={e => onPromptChange(e.target.value)}
            onFocus={handleFocus}
            placeholder="e.g., A majestic lion wearing a crown, vintage comic book style"
            className="box-border w-full resize-none rounded-lg border border-gray-300 p-2 text-base font-normal placeholder-gray-400 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
            rows={3}
            data-testid="prompt-textarea"
            autoFocus
          />
        )}
      </div>

      {/* Buttons - Show different combinations based on state */}
      {!shouldShowCompactMode && !isEditing && (
        <button
          onClick={handleGenerate}
          disabled={!prompt.trim() || isLoading || !hasCredits}
          className="bg-cta-gradient hover:bg-cta-gradient-hover mb-1 flex items-center justify-center gap-2 rounded-lg py-2 text-center text-xs font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-70"
          data-testid="generate-button"
        >
          {getGenerateButtonContent({
            isLoading,
            hasCredits,
            actionText: "Generate",
            loadingText: "Generating..."
          })}
        </button>
      )}

      {/* Edit Mode Buttons - Show when editing */}
      {isEditing && (
        <div className="flex gap-2">
          <button
            onClick={handleCancelEdit}
            className="mx-2 flex items-center justify-center gap-2 py-2 text-center text-sm font-semibold text-blue-700 transition-all"
            data-testid="cancel-edit-button"
          >
            Cancel Edit
          </button>
          <button
            onClick={handleGenerate}
            disabled={!prompt.trim() || isLoading || !hasCredits}
            className="bg-cta-gradient hover:bg-cta-gradient-hover flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-center text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-70"
            data-testid="generate-button-edit-mode"
          >
            {getGenerateButtonContent({
              isLoading,
              hasCredits,
              actionText: "Generate",
              loadingText: "Generating..."
            })}
          </button>
        </div>
      )}

      {/* Message Block */}
      {!shouldShowCompactMode && <ErrorMessage error={error} showNoCreditsMessage={!hasCredits} />}
    </div>
  );
}
